import { APP_INITIALIZER, ErrorHandler, NgModule, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PageContainerComponent } from './components/page-container/page-container.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatSidenavModule } from '@angular/material/sidenav';
import { AppRoutingModule } from './app-routing.module';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr
} from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { environment } from '../environments/environment';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ErrorPageComponent } from './error-page/error-page.component';
import { UrlInterceptor } from './http/url-interceptor';
import { CognitoService, TokenInterceptor } from '@odin/odin-authentication';
import { ElevatedMfaInterceptor } from '@odin/odin-authentication';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';

import {
  PaginationInterceptor,
  TransactionsFacade,
  SettlementsFacade,
  TransactionsLayoutModule,
} from '@odin/odin-transactions';

import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DashboardComponent } from './dashboard-page/dashboard.component';
import {
  MerchantChangeService,
  MerchantService,
  OdinCoreModule,
  type OdinFeatures,
  GlobalErrorHandler,
  TraceHttpInterceptor,
  RUM_ENV_CONFIG,
  type RumEnvironmentConfig,
  RouteTelemetryService,
  TRACE_ENV_CONFIG,
  type TraceEnvironmentConfig,
} from '@odin/odin-core';
import { MerchantUpdateService } from '@odin/odin-transactions';
import { VirtualTerminalPageComponent } from './virtual-terminal-page/virtual-terminal-page.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MerchantPickerComponent } from './merchant-picker/merchant-picker.component';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';

import { PeblPageComponent } from './pebl-page/pebl-page.component';
import { PeblConfirmationDialogComponent } from './pebl-confirmation-dialog/pebl-confirmation-dialog.component';
import { TransactionOutcomePageComponent } from './transaction-outcome-page/transaction-outcome-page.component';
import { DashboardMetricsComponent } from './dashboard-metrics/dashboard-metrics.component';
import { BrandingHelper } from './Services/branding-helper';
import { QrCodeComponent } from 'ng-qrcode';
import { MarkdownModule } from 'ngx-markdown';

@NgModule({
  declarations: [
    AppComponent,
    PageContainerComponent,
    ErrorPageComponent,
    DashboardComponent,
    VirtualTerminalPageComponent,
    MerchantPickerComponent,
    PeblPageComponent,
    PeblConfirmationDialogComponent,
    TransactionOutcomePageComponent,
    DashboardMetricsComponent,
  ],
  bootstrap: [AppComponent],
  exports: [
    PageContainerComponent,
    ErrorPageComponent,
    DashboardComponent,
    VirtualTerminalPageComponent,
    MerchantPickerComponent,
    PeblPageComponent,
    PeblConfirmationDialogComponent,
    TransactionOutcomePageComponent,
  ],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    FontAwesomeModule,
    MatSidenavModule,
    MatSlideToggleModule,
    MatButtonToggleModule,
    MatSnackBarModule,
    AppRoutingModule,
    MatDialogModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
    MatIconModule,
    MatMenuModule,
    MatSelectModule,
    MatCardModule,
    MatAutocompleteModule,
    MatInputModule,
    MatDividerModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot(),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: !environment.production,
      connectInZone: true,
    }),
    OdinCoreModule,
    TransactionsLayoutModule,
    QrCodeComponent,
    MarkdownModule.forRoot(),
  ],
  providers: [
    {
      provide: ErrorHandler,
      useExisting: GlobalErrorHandler,
    },
    {
      provide: TRACE_ENV_CONFIG,
      useValue: (environment as { trace?: TraceEnvironmentConfig }).trace ?? null,
    },
    {
      provide: RUM_ENV_CONFIG,
      useValue: (environment as { rum?: RumEnvironmentConfig }).rum ?? null,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (routeTelemetry: RouteTelemetryService) => () => routeTelemetry.init(),
      deps: [RouteTelemetryService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      multi: true,
    },
    TransactionsFacade,
    SettlementsFacade,
    MerchantService,
    MerchantChangeService,
    MerchantUpdateService,
    {
      provide: 'themes',
      useValue: BrandingHelper.currentBranding().availableThemes,
    },
    {
      provide: 'features',
      useValue: environment.featureToggles as OdinFeatures,
    },
    {
      provide: 'userPoolId',
      useValue: environment.userPoolId,
    },
    {
      provide: 'clientId',
      useValue: environment.appClientId,
    },
    {
      provide: 'hostedLoginUrl',
      useValue: environment.hostedLoginUrl,
    },
    {
      provide: 'authCallbackUrl',
      useValue: environment.authCallbackUrl,
    },
    {
      provide: 'congitoResource',
      useValue: environment.congitoResource,
    },
    {
      provide: 'cognitoAudience',
      useValue: environment.cognitoAudience,
    },
    {
      provide: 'clientRoot',
      useValue: environment.clientRoot,
    },
    {
      provide: 'apiRoot',
      useValue: environment.apiRoot,
    },
    {
      provide: 'baseAddress',
      useValue: environment.baseAddress,
    },
    {
      provide: 'xeroBaseAddress',
      useValue: environment.xeroBaseAddress,
    },
    {
      provide: 'notificationWebSocketUrl',
      useValue: environment.notificationWebSocketUrl,
    },
    {
      provide: 'getAccessToken',
      useFactory: (cognitoService: CognitoService) => () => cognitoService.getAccessToken(),
      deps: [CognitoService],
    },
    {
      provide: 'fuzzySearch',
      useValue: environment.featureToggles.fuzzySearch,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UrlInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PaginationInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ElevatedMfaInterceptor,
      multi: true,
    },
    // Last in the DI chain: sees the URL resolved by UrlInterceptor and records the reference before Token/MFA handlers. A DI interceptor (not functional) so lazy-module HttpClients from OdinCoreModule also get it.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TraceHttpInterceptor,
      multi: true,
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideZoneChangeDetection({ eventCoalescing: true }),
  ],
})
export class AppModule {}
